import React from 'react';

const getStoredUser = () => {
  try { return JSON.parse(sessionStorage.getItem('iad2_user') || 'null'); } catch { return null; }
};

const CHECK = (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 24, height: 24, borderRadius: '50%',
    background: 'var(--green)', color: 'white', fontSize: 14, fontWeight: 700,
  }}>✓</span>
);
const DASH = <span style={{ color: 'var(--gray-300)', fontSize: 20, lineHeight: 1 }}>—</span>;

interface Row {
  feature: string;
  user: boolean;
  superuser: boolean;
  admin: boolean;
}

interface Group {
  label: string;
  rows: Row[];
}

const GROUPS: Group[] = [
  {
    label: 'Contacts',
    rows: [
      { feature: 'View directory',         user: true,  superuser: true,  admin: true },
      { feature: 'View contact detail',    user: true,  superuser: true,  admin: true },
      { feature: 'Add contact',            user: false, superuser: true,  admin: true },
      { feature: 'Edit contact',           user: false, superuser: true,  admin: true },
      { feature: 'Archive / restore',      user: false, superuser: true,  admin: true },
      { feature: 'Mark as verified',       user: false, superuser: true,  admin: true },
      { feature: 'Flag contact',           user: true,  superuser: true,  admin: true },
      { feature: 'Resolve flag',           user: false, superuser: true,  admin: true },
      { feature: 'Send contact info',      user: true,  superuser: true,  admin: true },
      { feature: 'Export contact PDF',     user: true,  superuser: true,  admin: true },
    ],
  },
  {
    label: 'Agencies',
    rows: [
      { feature: 'View agencies',          user: true,  superuser: true,  admin: true },
      { feature: 'Create agency',          user: false, superuser: true,  admin: true },
      { feature: 'Edit agency',            user: false, superuser: true,  admin: true },
      { feature: 'Edit contact procedures',user: false, superuser: true,  admin: true },
      { feature: 'Associate contact',      user: false, superuser: true,  admin: true },
      { feature: 'Archive / reactivate',   user: false, superuser: false, admin: true },
    ],
  },
  {
    label: 'Lists',
    rows: [
      { feature: 'View lists',             user: true,  superuser: true,  admin: true },
      { feature: 'Create / edit / archive list', user: false, superuser: true, admin: true },
      { feature: 'Add / remove members',   user: false, superuser: true,  admin: true },
    ],
  },
  {
    label: 'Update Requests',
    rows: [
      { feature: 'View update requests',   user: false, superuser: true,  admin: true },
      { feature: 'Create update request',  user: false, superuser: true,  admin: true },
      { feature: 'Cancel update request',  user: false, superuser: true,  admin: true },
    ],
  },
  {
    label: 'Pending Changes',
    rows: [
      { feature: 'View pending changes',   user: false, superuser: true,  admin: true },
      { feature: 'Approve / reject',       user: false, superuser: true,  admin: true },
    ],
  },
  {
    label: 'Admin',
    rows: [
      { feature: 'KPI dashboard',          user: false, superuser: true,  admin: true },
      { feature: 'Audit log',              user: false, superuser: true,  admin: true },
      { feature: 'Manage users',           user: false, superuser: false, admin: true },
      { feature: 'Manage roles',           user: false, superuser: false, admin: true },
      { feature: 'Grant restricted access',user: false, superuser: true,  admin: true },
      { feature: 'Import contacts',        user: false, superuser: true,  admin: true },
      { feature: 'Export directory',       user: false, superuser: true,  admin: true },
    ],
  },
];

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  User:          { bg: 'var(--sky-50)', color: 'var(--electric)' },
  Superuser:     { bg: 'var(--amber-50)', color: 'var(--amber-text)' },
  Administrator: { bg: 'var(--electric)', color: 'var(--white)' },
};

const RoleCapabilitiesPage: React.FC = () => {
  const currentUser = getStoredUser();
  const role: string = currentUser?.role ?? 'User';
  const roleStyle = ROLE_COLORS[role] ?? ROLE_COLORS['User'];

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>Role Capabilities</h2>
        <span
          style={{
            background: roleStyle.bg,
            color: roleStyle.color,
            fontWeight: 700,
            fontSize: 12,
            padding: '3px 10px',
            borderRadius: 12,
            letterSpacing: '0.03em',
          }}
        >
          Your role: {role}
        </span>
      </div>

      {/* Note */}
      <div
        style={{
          borderLeft: '4px solid var(--electric)',
          background: 'var(--sky-50)',
          padding: '10px 14px',
          borderRadius: 4,
          marginBottom: 20,
          fontSize: 14,
          color: 'var(--nycem-blue-800)',
        }}
      >
        Permissions are cumulative — Superuser has all User capabilities plus more, and Administrator has all Superuser capabilities plus more.
      </div>

      {/* Matrix table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--electric)', color: 'var(--white)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 14, fontWeight: 700, width: '45%' }}>
                Feature
              </th>
              <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 14, fontWeight: 700, width: '18%' }}>
                User
              </th>
              <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 14, fontWeight: 700, width: '18%' }}>
                Superuser
              </th>
              <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 14, fontWeight: 700, width: '19%' }}>
                Administrator
              </th>
            </tr>
          </thead>
          <tbody>
            {GROUPS.map((group) => (
              <React.Fragment key={group.label}>
                <tr style={{ background: 'var(--gray-100)' }}>
                  <td
                    colSpan={4}
                    style={{
                      padding: '7px 16px',
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      color: 'var(--gray-600)',
                    }}
                  >
                    {group.label}
                  </td>
                </tr>
                {group.rows.map((row, idx) => (
                  <tr
                    key={row.feature}
                    style={{ background: idx % 2 === 0 ? 'var(--white)' : 'var(--gray-50)' }}
                  >
                    <td style={{ padding: '9px 16px', fontSize: 14, color: 'var(--gray-800)' }}>
                      {row.feature}
                    </td>
                    <td style={{ padding: '9px 16px', textAlign: 'center' }}>
                      {row.user ? CHECK : DASH}
                    </td>
                    <td style={{ padding: '9px 16px', textAlign: 'center' }}>
                      {row.superuser ? CHECK : DASH}
                    </td>
                    <td style={{ padding: '9px 16px', textAlign: 'center' }}>
                      {row.admin ? CHECK : DASH}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoleCapabilitiesPage;
